import { ERoundState } from "../../core/round/interfaces";
import { ISound, SoundService } from "@karma-games/angular-devkit/src/lib/modules/sound";
import { soundModels } from "../../shared/services/sound/sound-models";

export class CrocoGameAudio {
    private multiplierSoundNameArr: string[] = [
        "GameAnimationMultiplierSound",
        "GameAnimationMultiplierSound-1",
        "GameAnimationMultiplierSound-2",
        "GameAnimationMultiplierSound-3"
    ];
    private lastMultiplierSoundIndex: number | undefined = undefined;
    private mainGameSoundId: string | null = null;
    private gameIdleSoundId: string | null = null;
    private gameEndSoundId: string | null = null;
    private bigMultiplierSoundId: string | null = null;
    private multiplierSoundId: string | null = null;
    private stepsSoundId: string | null = null;
    private gameSoundEnabled: boolean = false;
    private soundEnabled: boolean = false;
    constructor(private soundService: SoundService) {
    }

    onSoundSettingChange(enabled: boolean) {
        this.soundEnabled = enabled;
        if (!enabled) {
            if (this.multiplierSoundId) {
                this.soundService.mute(this.multiplierSoundId);
            }
            if (this.bigMultiplierSoundId) {
                this.soundService.mute(this.bigMultiplierSoundId);
            }
        } else {
            if (this.multiplierSoundId) {
                this.soundService.unmute(this.multiplierSoundId);
            }
            if (this.bigMultiplierSoundId) {
                this.soundService.unmute(this.bigMultiplierSoundId);
            }
        }
    }
    onGameSoundSettingChange(enabled: boolean) {
        this.gameSoundEnabled = enabled;
        if (!enabled) {
            if (this.gameEndSoundId) {
                this.soundService.mute(this.gameEndSoundId);
            }
            if (this.mainGameSoundId) {
                this.soundService.mute(this.mainGameSoundId);
            }
            if (this.gameIdleSoundId) {
                this.soundService.mute(this.gameIdleSoundId);
            }
            if (this.stepsSoundId) {
                this.soundService.mute(this.stepsSoundId);
            }
        } else {
            if (this.gameEndSoundId) {
                this.soundService.unmute(this.gameEndSoundId);
            }
            if (this.mainGameSoundId && !this.gameIdleSoundId) {
                this.soundService.unmute(this.mainGameSoundId);
            }
            if (this.gameIdleSoundId) {
                this.soundService.unmute(this.gameIdleSoundId);
            }
            if (this.stepsSoundId) {
                this.soundService.unmute(this.stepsSoundId);
            }
        }
    }

    public handleAnimationAudio(roundState: ERoundState): void {
        switch (roundState) {
            case ERoundState.Idle:
                if (this.gameEndSoundId) {
                    this.soundService.stop(this.gameEndSoundId);
                    this.gameEndSoundId = null;
                }
                if (!this.gameIdleSoundId) {
                    this.gameIdleSoundId = this.soundService.play("GameAnimationIdleSound", {
                        interruptable: false,
                        loop: true,
                        bypassChecks: true
                    });
                }
                if (!this.mainGameSoundId) {
                    this.mainGameSoundId = this.soundService.play("GameAnimationMainSound", {
                        interruptable: false,
                        loop: true,
                        bypassChecks: true,
                        volume: 0
                    });
                    this.soundService.mute(<string>this.mainGameSoundId);
                }
                if (!this.gameSoundEnabled) {
                    this.soundService.mute(<string>this.gameIdleSoundId);
                } else {
                    this.soundService.unmute(<string>this.gameIdleSoundId);
                }
                break;
            case ERoundState.Playing:
                if (!this.mainGameSoundId) {
                    this.mainGameSoundId = this.soundService.play("GameAnimationMainSound", {
                        interruptable: false,
                        loop: true,
                        bypassChecks: true
                    });
                } else {
                    if (this.gameSoundEnabled) {
                        this.soundService.unmute(<string>this.mainGameSoundId);
                    }
                    this.soundService.fade(<string>this.mainGameSoundId, {
                        from: 0,
                        to: soundModels.find(sm => sm.id === "GameAnimationMainSound")?.defaultVolume ?? .15,
                        duration: 1500
                    });
                }
                if (this.gameIdleSoundId) {
                    this.soundService.stop(this.gameIdleSoundId, {
                        from: soundModels.find(sm => sm.id === "GameAnimationIdleSound")?.defaultVolume ?? .15,
                        to: 0,
                        duration: 1500
                    });
                    this.gameIdleSoundId = null;
                }
                if (!this.stepsSoundId) {
                    this.stepsSoundId = this.soundService.play("GameAnimationStepsSound", {
                        interruptable: false,
                        loop: true,
                        bypassChecks: true
                    });
                }
                if (!this.gameSoundEnabled) {
                    this.soundService.mute(<string>this.mainGameSoundId);
                    this.soundService.mute(<string>this.stepsSoundId);
                } else {
                    this.soundService.unmute(<string>this.mainGameSoundId);
                    this.soundService.unmute(<string>this.stepsSoundId);
                }
                break;
            case ERoundState.Ended:
                this.lastMultiplierSoundIndex = undefined;
                if (this.mainGameSoundId) {
                    this.soundService.stop(this.mainGameSoundId, {
                        from: soundModels.find(sm => sm.id === "GameAnimationMainSound")?.defaultVolume ?? .15,
                        to: 0,
                        duration: 500
                    });
                    this.mainGameSoundId = null;
                }
                if (this.stepsSoundId) {
                    this.soundService.stop(this.stepsSoundId, {
                        from: soundModels.find(sm => sm.id === "GameAnimationStepsSound")?.defaultVolume ?? .3,
                        to: 0,
                        duration: 500
                    });
                    this.stepsSoundId = null;
                }
                if (!this.gameEndSoundId) {
                    this.gameEndSoundId = this.soundService.play("GameAnimationFallSound", {
                        interruptable: false,
                        loop: false,
                        bypassChecks: true
                    });
                }
                if (!this.gameSoundEnabled) {
                    this.soundService.mute(<string>this.gameEndSoundId);
                } else {
                    this.soundService.unmute(<string>this.gameEndSoundId);
                }
                break;
            default:
                break;
        }
    }

    public onMultiplierUpdate(multiplier: number): void {
        if (this.mainGameSoundId && this.stepsSoundId && (multiplier / 500 + 1 <= 1.4)) {
            this.soundService.changeRate(this.mainGameSoundId, multiplier / 500 + 1);
            this.soundService.changeRate(this.stepsSoundId, multiplier / 500 + 1);
        }
    }

    public multiplierSound(bigMultiplier: boolean = false): void {
        if (bigMultiplier && this.soundEnabled && !this.bigMultiplierSoundId) {
            this.multiplierSoundId = this.soundService.play("GameAnimationBigMultiplierSound", {
                interruptable: false,
                loop: false,
                bypassChecks: true
            });
            this.soundService.onSoundEnd((sound: ISound) => {
                if (sound.id === this.multiplierSoundId) {
                    this.multiplierSoundId = null;
                }
            });
        } else if (this.soundEnabled && !this.multiplierSoundId) {
            if (this.lastMultiplierSoundIndex === undefined) {
                this.lastMultiplierSoundIndex = 0;
            } else if (this.lastMultiplierSoundIndex < this.multiplierSoundNameArr.length - 1) {
                this.lastMultiplierSoundIndex += 1;
            } else {
                this.lastMultiplierSoundIndex = this.multiplierSoundNameArr.length - 1;
            }
            this.multiplierSoundId = this.soundService.play(this.multiplierSoundNameArr[this.lastMultiplierSoundIndex], {
                interruptable: false,
                loop: false,
                bypassChecks: true
            });
            this.soundService.onSoundEnd((sound: ISound) => {
                if (sound.id === this.multiplierSoundId) {
                    this.multiplierSoundId = null;
                }
            });
        }
    }
}
